/**
 * @deprecated This service is deprecated and will be removed in a future version.
 * 
 * **Migration Guide:**
 * - For system/admin templates: Use `systemTemplateService` from './systemTemplateService'
 * - For user templates: Use `userTemplateService` from './userTemplateService'  
 * - For unified access: Use `unifiedTemplateService` from './unifiedTemplateService'
 * 
 * **Key Changes:**
 * - System templates now in `email_templates` collection (createdBy: 'system' | 'admin')
 * - User templates now in `user_email_templates` collection (with workspaceId)
 * - User templates require workspaceId for workspace isolation
 * 
 * This compatibility layer is maintained for backward compatibility only.
 */

import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, orderBy, Timestamp, writeBatch, where, QueryDocumentSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';

export interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  subject?: string;
  category: 'inspirational' | 'basic' | 'yours';
  previewImage: string;
  htmlContent: string;
  htmlBody?: string;
  textBody?: string;
  cssContent?: string;
  tags: string[];
  variables?: string[];
  uploadedBy: string;
  uploadedAt: Date;
  updatedAt?: Date;
  isActive: boolean;
  downloadCount: number;
  lastUsed?: Date;
  isPaid?: boolean;
  price?: number;
  // Editor design JSON for drag-and-drop builder
  designJson?: unknown;
  displayOrder?: number;
  // Featured on discover page
  featuredOnDiscoverPage?: boolean;
  discoverPageOrder?: number;
}

export interface EmailTemplateVariable {
  name: string;
  description: string;
  example: string;
  required: boolean;
}

export interface TemplateUploadData {
  name: string;
  description: string;
  htmlContent: string;
  cssContent?: string;
  tags: string[];
  previewImage?: File;
  isPaid?: boolean;
  price?: number;
}

export class EmailTemplateService {
  private templates: EmailTemplate[] = [];
  private storageKey = 'email_templates';

  static TEMPLATE_VARIABLES: EmailTemplateVariable[] = [
    { name: 'userName', description: 'User\'s full name', example: 'John Doe', required: true },
    { name: 'userEmail', description: 'User\'s email address', example: 'john@example.com', required: true },
    { name: 'companyName', description: 'Company name', example: 'Scubamail', required: false },
    { name: 'verificationCode', description: 'Email verification code', example: '123456', required: false },
    { name: 'resetCode', description: 'Password reset code', example: 'ABC123', required: false },
    { name: 'loginUrl', description: 'Login page URL', example: 'https://app.scubamail.io/login', required: false },
    { name: 'supportEmail', description: 'Support email address', example: 'support@scubamail.io', required: false },
    { name: 'unsubscribeUrl', description: 'Unsubscribe URL', example: 'https://app.scubamail.io/api/unsubscribe-one-click', required: false }
  ];

  constructor() {
    this.loadTemplates();
    this.initializeDefaultTemplates();
  }

  private loadTemplates(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.templates = JSON.parse(stored).map((template: any) => ({
          ...template,
          uploadedAt: new Date(template.uploadedAt),
          lastUsed: template.lastUsed ? new Date(template.lastUsed) : undefined
        }));
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      this.templates = [];
    }
  }

  private saveTemplates(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.templates));
    } catch (error) {
      console.error('Error saving templates:', error);
    }
  }

  private initializeDefaultTemplates(): void {
    if (this.templates.length === 0) {
      const defaultTemplates: EmailTemplate[] = [
        {
          id: 'announce-copy',
          name: 'Announce (copy)',
          description: 'Clean announcement template with call-to-action',
          category: 'yours',
          previewImage: '',
          htmlContent: `
            <div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif; background: #ffffff;">
              <div style="padding: 20px; text-align: center;">
                <img src="https://via.placeholder.com/300x150/667eea/ffffff?text=Announcement" alt="Announcement" style="width: 100%; max-width: 300px; height: auto; border-radius: 8px; margin-bottom: 20px;">
                <h1 style="color: #333; font-size: 24px; margin-bottom: 16px;">The announcement template</h1>
                <p style="color: #666; font-size: 16px; line-height: 1.5; margin-bottom: 24px;">This is a clean and professional template perfect for making important announcements to your audience.</p>
                <a href="#" style="background: #667eea; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">Call to action</a>
              </div>
            </div>
          `,
          tags: ['announcement', 'clean', 'professional'],
          uploadedBy: 'system',
          uploadedAt: new Date('2024-09-13T18:22:00'),
          updatedAt: new Date('2024-09-13T18:22:00'),
          isActive: true,
          downloadCount: 0
        },
        {
          id: 'black-friday-sale-copy',
          name: 'Black Friday sale (copy)',
          description: 'Bold promotional template for sales events',
          category: 'yours',
          previewImage: '',
          htmlContent: `
            <div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif; background: #2d1b69;">
              <div style="padding: 20px; text-align: center; color: white;">
                <h1 style="color: #ff6b6b; font-size: 28px; margin-bottom: 16px; font-weight: bold;">BLACK FRIDAY SALE IS ON</h1>
                <h2 style="color: white; font-size: 20px; margin-bottom: 24px;">20% OFF ON ALL OUR LIMITED EDITION NEW SODAS</h2>
                <a href="#" style="background: #ff6b6b; color: white; padding: 16px 32px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 18px; margin-bottom: 24px;">STOCK UP & SAVE</a>
                <div style="display: flex; justify-content: center; gap: 20px; margin-top: 20px;">
                  <div style="text-align: center;">
                    <div style="width: 60px; height: 80px; background: #ff9500; border-radius: 8px; margin: 0 auto 8px;"></div>
                    <p style="color: white; font-size: 14px; margin: 0;">Orange Burst</p>
                  </div>
                  <div style="text-align: center;">
                    <div style="width: 60px; height: 80px; background: #ff1744; border-radius: 8px; margin: 0 auto 8px;"></div>
                    <p style="color: white; font-size: 14px; margin: 0;">Berry Bliss</p>
                  </div>
                  <div style="text-align: center;">
                    <div style="width: 60px; height: 80px; background: #4caf50; border-radius: 8px; margin: 0 auto 8px;"></div>
                    <p style="color: white; font-size: 14px; margin: 0;">Lemon & Lime</p>
                  </div>
                </div>
              </div>
            </div>
          `,
          tags: ['sale', 'promotion', 'retail', 'black-friday'],
          uploadedBy: 'system',
          uploadedAt: new Date('2024-09-13T18:24:00'),
          updatedAt: new Date('2024-09-13T18:24:00'),
          isActive: true,
          downloadCount: 0
        },
        {
          id: 'artist-welcome-copy',
          name: 'Artist welcome (copy)',
          description: 'Creative welcome template for artists and musicians',
          category: 'yours',
          previewImage: '',
          htmlContent: `
            <div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif; background: #1a1a1a;">
              <div style="padding: 20px; text-align: center; color: white;">
                <img src="https://via.placeholder.com/300x200/333333/ffffff?text=Band+Photo" alt="Band" style="width: 100%; max-width: 300px; height: auto; border-radius: 8px; margin-bottom: 20px; filter: grayscale(100%);">
                <h1 style="color: #ff6b6b; font-size: 24px; margin-bottom: 16px; font-weight: bold;">WELCOME TO THE NIGHT ECHOES' FAMILY</h1>
                <p style="color: #cccccc; font-size: 16px; line-height: 1.5; margin-bottom: 24px;">Thank you for joining our musical journey. Get ready for exclusive content, early access to new releases, and behind-the-scenes content.</p>
                <a href="#" style="background: #ff6b6b; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">EXPLORE OUR MUSIC</a>
              </div>
            </div>
          `,
          tags: ['welcome', 'music', 'artist', 'creative'],
          uploadedBy: 'system',
          uploadedAt: new Date('2024-09-13T21:05:00'),
          updatedAt: new Date('2024-09-13T21:05:00'),
          isActive: true,
          downloadCount: 0
        },
        {
          id: 'basic-newsletter',
          name: 'Basic Newsletter',
          description: 'Simple and clean newsletter template',
          category: 'basic',
          previewImage: '',
          htmlContent: `
            <div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif; background: #ffffff;">
              <div style="padding: 20px;">
                <h1 style="color: #333; font-size: 24px; margin-bottom: 16px;">Newsletter Title</h1>
                <p style="color: #666; font-size: 16px; line-height: 1.5; margin-bottom: 20px;">This is a simple newsletter template that you can customize for your needs.</p>
                <div style="border-top: 1px solid #eee; padding-top: 20px; margin-top: 20px;">
                  <p style="color: #999; font-size: 14px;">© 2024 Your Company. All rights reserved.</p>
                </div>
              </div>
            </div>
          `,
          tags: ['newsletter', 'basic', 'simple'],
          uploadedBy: 'system',
          uploadedAt: new Date('2024-09-10T10:00:00'),
          isActive: true,
          downloadCount: 0
        },
        {
          id: 'inspirational-quote',
          name: 'Inspirational Quote',
          description: 'Motivational template with inspirational content',
          category: 'inspirational',
          previewImage: '',
          htmlContent: `
            <div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);">
              <div style="padding: 40px 20px; text-align: center; color: white;">
                <h1 style="font-size: 28px; margin-bottom: 20px; font-style: italic;">"The only way to do great work is to love what you do."</h1>
                <p style="font-size: 18px; margin-bottom: 30px;">- Steve Jobs</p>
                <a href="#" style="background: rgba(255,255,255,0.2); color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold; border: 2px solid white;">Get Inspired</a>
              </div>
            </div>
          `,
          tags: ['inspirational', 'quote', 'motivational'],
          uploadedBy: 'system',
          uploadedAt: new Date('2024-09-12T15:30:00'),
          isActive: true,
          downloadCount: 0
        }
      ];

      this.templates = defaultTemplates;
      this.saveTemplates();
    }
  }

  private firestoreDocToTemplate(d: QueryDocumentSnapshot): EmailTemplate {
    const data = d.data();
    return {
      id: d.id,
      name: data.name,
      description: data.description,
      subject: data.subject,
      category: data.category,
      previewImage: data.previewImage,
      htmlContent: data.htmlContent,
      htmlBody: data.htmlBody,
      textBody: data.textBody,
      cssContent: data.cssContent,
      tags: data.tags || [],
      variables: data.variables || [],
      uploadedBy: data.createdBy || data.uploadedBy,
      uploadedAt: data.createdAt?.toDate() || data.uploadedAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate(),
      isActive: data.isActive !== false,
      downloadCount: data.downloadCount || 0,
      lastUsed: data.lastUsed?.toDate(),
      designJson: data.designJson,
      displayOrder: data.displayOrder,
      featuredOnDiscoverPage: data.featuredOnDiscoverPage || false,
      discoverPageOrder: data.discoverPageOrder
    };
  }

  // Get all templates the current user is allowed to see. Admins can list the full collection; others use scoped queries
  // so a single mixed query does not fail when other users' private docs exist.
  async getAllTemplates(): Promise<EmailTemplate[]> {
    const sortTemplates = (list: EmailTemplate[]) =>
      list.sort((a, b) => {
        const orderA = a.displayOrder !== undefined && a.displayOrder !== null ? a.displayOrder : Infinity;
        const orderB = b.displayOrder !== undefined && b.displayOrder !== null ? b.displayOrder : Infinity;
        if (orderA !== orderB) return orderA - orderB;
        const dateA = a.uploadedAt instanceof Date ? a.uploadedAt.getTime() : 0;
        const dateB = b.uploadedAt instanceof Date ? b.uploadedAt.getTime() : 0;
        return dateB - dateA;
      });

    try {
      const col = collection(db, 'email_templates');
      try {
        const querySnapshot = await getDocs(query(col, orderBy('createdAt', 'desc')));
        const firestoreTemplates: EmailTemplate[] = [];
        querySnapshot.forEach((docSnap) => {
          firestoreTemplates.push(this.firestoreDocToTemplate(docSnap));
        });
        this.templates = firestoreTemplates;
        this.saveTemplates();
        return sortTemplates(firestoreTemplates);
      } catch {
        // Non-admin (or missing index): merge allowed slices only
        const uid = auth.currentUser?.uid;
        const queries = [query(col, where('createdBy', 'in', ['system', 'admin']))];
        if (uid) {
          queries.push(query(col, where('createdBy', '==', uid)));
          queries.push(query(col, where('userId', '==', uid)));
          queries.push(query(col, where('isPublic', '==', true)));
        }
        const snapshots = await Promise.all(queries.map((q) => getDocs(q)));
        const byId = new Map<string, EmailTemplate>();
        for (const snap of snapshots) {
          snap.forEach((docSnap) => {
            byId.set(docSnap.id, this.firestoreDocToTemplate(docSnap));
          });
        }
        const firestoreTemplates = Array.from(byId.values());
        this.templates = firestoreTemplates;
        this.saveTemplates();
        return sortTemplates(firestoreTemplates);
      }
    } catch (error) {
      console.error('Error loading templates from Firestore:', error);
      return [];
    }
  }

  // Get templates by category
  getTemplatesByCategory(category: string): EmailTemplate[] {
    return this.templates.filter(template => 
      template.category === category && template.isActive
    );
  }

  // Get inspirational templates (admin uploaded + default)
  getInspirationalTemplates(): EmailTemplate[] {
    return this.templates.filter(template => 
      template.category === 'inspirational' && template.isActive
    );
  }

  // Get featured templates for discover page
  async getFeaturedTemplatesForDiscoverPage(): Promise<EmailTemplate[]> {
    try {
      const allTemplates = await this.getAllTemplates();
      const featuredTemplates = allTemplates
        .filter(template => template.featuredOnDiscoverPage && template.isActive)
        .sort((a, b) => (a.discoverPageOrder ?? 999) - (b.discoverPageOrder ?? 999));
      return featuredTemplates.slice(0, 10); // Limit to 10 templates
    } catch (error) {
      console.error('Error loading featured templates:', error);
      return [];
    }
  }

  // Get template by ID
  getTemplateById(id: string): EmailTemplate | undefined {
    return this.templates.find(template => template.id === id);
  }

  // Clear all templates and cache
  clearCache(): void {
    this.templates = [];
    this.saveTemplates();
  }

  // Upload new template
  async uploadTemplate(templateData: TemplateUploadData, uploadedBy: string): Promise<EmailTemplate> {
    try {
      console.log('Starting template upload:', { templateData, uploadedBy });
      
      // Save to Firestore
      const templateDoc = {
        name: templateData.name,
        description: templateData.description,
        category: 'inspirational',
        previewImage: templateData.previewImage ? await this.processImageFile(templateData.previewImage) : '/api/placeholder/300/200',
        htmlContent: templateData.htmlContent,
        cssContent: templateData.cssContent || '',
        tags: templateData.tags,
        uploadedBy,
        uploadedAt: Timestamp.now(),
        isActive: true,
        downloadCount: 0,
        lastUsed: null,
        isPaid: templateData.isPaid || false,
        price: templateData.price || 0
      };

      console.log('Template document to save:', templateDoc);
      const docRef = await addDoc(collection(db, 'email_templates'), templateDoc);
      console.log('Template saved to Firestore with ID:', docRef.id);
      
      const newTemplate: EmailTemplate = {
        id: docRef.id,
        name: templateData.name,
        description: templateData.description,
        category: 'inspirational',
        previewImage: templateData.previewImage ? await this.processImageFile(templateData.previewImage) : '/api/placeholder/300/200',
        htmlContent: templateData.htmlContent,
        cssContent: templateData.cssContent,
        tags: templateData.tags,
        uploadedBy,
        uploadedAt: new Date(),
        isActive: true,
        downloadCount: 0,
        isPaid: templateData.isPaid || false,
        price: templateData.price || 0
      };

      // Update local cache
      this.templates.unshift(newTemplate);
      this.saveTemplates();
      
      return newTemplate;
    } catch (error) {
      console.error('Error uploading template to Firestore:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        code: (error as any)?.code,
        stack: error instanceof Error ? error.stack : undefined
      });
      // Fallback to local storage
      const newTemplate: EmailTemplate = {
        id: `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: templateData.name,
        description: templateData.description,
        category: 'inspirational',
        previewImage: templateData.previewImage ? await this.processImageFile(templateData.previewImage) : '/api/placeholder/300/200',
        htmlContent: templateData.htmlContent,
        cssContent: templateData.cssContent,
        tags: templateData.tags,
        uploadedBy,
        uploadedAt: new Date(),
        isActive: true,
        downloadCount: 0
      };

      this.templates.unshift(newTemplate);
      this.saveTemplates();
      return newTemplate;
    }
  }

  // Process image file to base64 or URL
  private async processImageFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target?.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Update template
  async updateTemplate(id: string, updates: Partial<EmailTemplate>): Promise<boolean> {
    try {
      // Update in Firestore
      const templateRef = doc(db, 'email_templates', id);
      const firestoreUpdates: any = { ...updates };
      
      // Convert Date objects to Timestamps for Firestore
      if (updates.uploadedAt) {
        firestoreUpdates.uploadedAt = Timestamp.fromDate(updates.uploadedAt);
      }
      if (updates.updatedAt) {
        firestoreUpdates.updatedAt = Timestamp.fromDate(updates.updatedAt);
      }
      if (updates.lastUsed) {
        firestoreUpdates.lastUsed = Timestamp.fromDate(updates.lastUsed);
      }
      
      await updateDoc(templateRef, firestoreUpdates);
      
      // Update local cache
      const index = this.templates.findIndex(template => template.id === id);
      if (index !== -1) {
        this.templates[index] = { ...this.templates[index], ...updates };
        this.saveTemplates();
      }
      
      return true;
    } catch (error) {
      console.error('Error updating template in Firestore:', error);
      // Fallback to local storage
      const index = this.templates.findIndex(template => template.id === id);
      if (index !== -1) {
        this.templates[index] = { ...this.templates[index], ...updates };
        this.saveTemplates();
        return true;
      }
      return false;
    }
  }

  // Delete template
  async deleteTemplate(id: string): Promise<boolean> {
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'email_templates', id));
      
      // Update local cache
      const index = this.templates.findIndex(template => template.id === id);
      if (index !== -1) {
        this.templates.splice(index, 1);
        this.saveTemplates();
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting template from Firestore:', error);
      // Fallback to local storage
      const index = this.templates.findIndex(template => template.id === id);
      if (index !== -1) {
        this.templates.splice(index, 1);
        this.saveTemplates();
        return true;
      }
      return false;
    }
  }

  // Delete all templates
  async deleteAllTemplates(): Promise<number> {
    try {
      // Get all templates from Firestore
      const templatesQuery = query(collection(db, 'email_templates'));
      const querySnapshot = await getDocs(templatesQuery);
      
      if (querySnapshot.empty) {
        console.log('No templates to delete');
        // Clear local cache anyway
        this.templates = [];
        this.saveTemplates();
        return 0;
      }

      // Delete in batches (Firestore batch limit is 500)
      const batchSize = 500;
      const docs = querySnapshot.docs;
      let totalDeleted = 0;

      for (let i = 0; i < docs.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchDocs = docs.slice(i, i + batchSize);

        batchDocs.forEach((docSnapshot) => {
          batch.delete(docSnapshot.ref);
        });

        await batch.commit();
        totalDeleted += batchDocs.length;
        console.log(`Deleted batch ${Math.floor(i / batchSize) + 1}: ${batchDocs.length} templates`);
      }

      // Clear local cache
      this.templates = [];
      this.saveTemplates();

      console.log(`Successfully deleted ${totalDeleted} templates`);
      return totalDeleted;
    } catch (error) {
      console.error('Error deleting all templates:', error);
      throw error;
    }
  }

  // Toggle template active status
  async toggleTemplateStatus(id: string): Promise<boolean> {
    const template = this.templates.find(t => t.id === id);
    if (template) {
      const newStatus = !template.isActive;
      
      try {
        // Update in Firestore
        const templateRef = doc(db, 'email_templates', id);
        await updateDoc(templateRef, { 
          isActive: newStatus,
          updatedAt: Timestamp.now()
        });
        
        // Update local cache
        template.isActive = newStatus;
        this.saveTemplates();
        return true;
      } catch (error) {
        console.error('Error updating template status in Firestore:', error);
        // Fallback to local storage
        template.isActive = newStatus;
        this.saveTemplates();
        return true;
      }
    }
    return false;
  }

  // Increment download count
  async incrementDownloadCount(id: string): Promise<void> {
    const template = this.templates.find(t => t.id === id);
    if (template) {
      const newCount = template.downloadCount + 1;
      const now = new Date();
      
      try {
        // Update in Firestore
        const templateRef = doc(db, 'email_templates', id);
        await updateDoc(templateRef, { 
          downloadCount: newCount,
          lastUsed: Timestamp.fromDate(now)
        });
        
        // Update local cache
        template.downloadCount = newCount;
        template.lastUsed = now;
        this.saveTemplates();
      } catch (error) {
        console.error('Error updating download count in Firestore:', error);
        // Fallback to local storage
        template.downloadCount = newCount;
        template.lastUsed = now;
        this.saveTemplates();
      }
    }
  }

  // Search templates
  searchTemplates(query: string, category?: string): EmailTemplate[] {
    let filtered = this.templates.filter(template => template.isActive);
    
    if (category) {
      filtered = filtered.filter(template => template.category === category);
    }

    if (query) {
      const searchQuery = query.toLowerCase();
      filtered = filtered.filter(template => 
        template.name.toLowerCase().includes(searchQuery) ||
        template.description.toLowerCase().includes(searchQuery) ||
        template.tags.some(tag => tag.toLowerCase().includes(searchQuery))
      );
    }

    return filtered;
  }

  // Get template statistics
  getTemplateStats(): {
    total: number;
    byCategory: Record<string, number>;
    mostDownloaded: EmailTemplate[];
    recentlyAdded: EmailTemplate[];
  } {
    const activeTemplates = this.templates.filter(t => t.isActive);
    
    const byCategory = activeTemplates.reduce((acc, template) => {
      acc[template.category] = (acc[template.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const mostDownloaded = [...activeTemplates]
      .sort((a, b) => b.downloadCount - a.downloadCount)
      .slice(0, 5);

    const recentlyAdded = [...activeTemplates]
      .sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime())
      .slice(0, 5);

    return {
      total: activeTemplates.length,
      byCategory,
      mostDownloaded,
      recentlyAdded
    };
  }

  // Export templates (for backup)
  exportTemplates(): string {
    return JSON.stringify(this.templates, null, 2);
  }

  // Import templates (for restore)
  importTemplates(jsonData: string): boolean {
    try {
      const importedTemplates = JSON.parse(jsonData);
      if (Array.isArray(importedTemplates)) {
        this.templates = importedTemplates.map((template: any) => ({
          ...template,
          uploadedAt: new Date(template.uploadedAt),
          lastUsed: template.lastUsed ? new Date(template.lastUsed) : undefined
        }));
        this.saveTemplates();
        return true;
      }
    } catch (error) {
      console.error('Error importing templates:', error);
    }
    return false;
  }

  // Process template with variables
  static processTemplate(template: EmailTemplate, variables: Record<string, string>): {
    subject: string;
    htmlBody: string;
    textBody: string;
  } {
    const processContent = (content: string): string => {
      let processed = content;
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
        processed = processed.replace(regex, value);
      });
      return processed;
    };

    return {
      subject: processContent(template.subject || ''),
      htmlBody: processContent(template.htmlBody || template.htmlContent || ''),
      textBody: processContent(template.textBody || '')
    };
  }
}

export const emailTemplateService = new EmailTemplateService();